import React, { useState, useEffect } from 'react';
import { UserMinus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { employeeApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * The one place an exit is processed.
 *
 * Deliberately NOT part of the employee edit form. An exit is an event with
 * its own dates and its own consequences — the notice period, the vacancy it
 * raises, the Requirement TAT that follows — and burying it in a status
 * dropdown is what made it possible to mark somebody gone with no reason, no
 * last working day and no seat left behind them.
 *
 * Everything after submit is automatic: the employee stays in the Employees
 * list and the org chart, badged "On Notice Period", until their last working
 * day, when they move to Ex-Employees on their own.
 */

const todayIso = () => new Date().toLocaleDateString('en-CA');   // local YYYY-MM-DD

export default function ResignDialog({ employee, open, onOpenChange, onDone }) {
  const [f, setF] = useState({
    resignation_date: todayIso(), last_working_day: '',
    exit_reason: 'Resigned', exit_remarks: '', note: '',
  });
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  // Fresh dates every time the dialog is opened for somebody.
  useEffect(() => {
    if (!open) return;
    setF({ resignation_date: todayIso(), last_working_day: '',
           exit_reason: 'Resigned', exit_remarks: '', note: '' });
    setTouched(false);
  }, [open, employee?.id]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const reasonMissing = !f.exit_remarks.trim();
  const datesWrong = !!(f.last_working_day && f.last_working_day < f.resignation_date);
  const noLwd = !f.last_working_day;

  // Shown live, so HR can see what the dates mean before committing to them.
  const preview = (() => {
    if (noLwd || datesWrong) return null;
    const lwd = new Date(`${f.last_working_day}T00:00:00`);
    const today = new Date(`${todayIso()}T00:00:00`);
    const days = Math.round((lwd - today) / 86400000);
    const pretty = lwd.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    if (days > 0) {
      return `${employee?.name} stays in Employees and in the Org Chart, badged ON NOTICE PERIOD, `
           + `for ${days} more day${days === 1 ? '' : 's'}, and moves to Ex-Employees on ${pretty}.`;
    }
    return `The last working day has already passed, so ${employee?.name} moves to Ex-Employees now.`;
  })();

  const submit = async () => {
    setTouched(true);
    if (reasonMissing) { toast.error('Reason of Leaving is required'); return; }
    if (noLwd) { toast.error('Pick the Last Working Day'); return; }
    if (datesWrong) { toast.error('The Last Working Day cannot be before the Resignation Date'); return; }
    setSaving(true);
    try {
      const r = await employeeApi.resign(employee.id, f);
      toast.success(
        r.data.employment_state === 'On Notice Period'
          ? `${employee.name} is on notice until ${f.last_working_day}. A vacancy has been raised.`
          : `${employee.name} has been moved to Ex-Employees. A vacancy has been raised.`);
      onOpenChange(false);
      onDone?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record the resignation');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="resign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserMinus className="w-5 h-5 text-[#E11D2A]" />
            Resign — {employee?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-[#5A6377]">
            {employee?.designation}{employee?.department ? ` · ${employee.department}` : ''}
            {employee?.unit ? ` · ${employee.unit}` : ''}
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Resignation Date *</Label>
              <Input data-testid="resign-date" type="date" value={f.resignation_date}
                     onChange={(e) => set('resignation_date', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Last Working Day *</Label>
              <Input data-testid="resign-lwd" type="date" value={f.last_working_day}
                     min={f.resignation_date}
                     onChange={(e) => set('last_working_day', e.target.value)}
                     className={(touched && (noLwd || datesWrong)) ? 'border-[#E11D2A]' : ''} />
              {touched && noLwd && (
                <p data-testid="resign-lwd-error" className="text-[11px] text-[#E11D2A] mt-1">
                  Required — this date is what moves them to Ex-Employees.
                </p>
              )}
              {datesWrong && (
                <p className="text-[11px] text-[#E11D2A] mt-1">
                  Cannot be before the resignation date.
                </p>
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Exit type</Label>
            <select data-testid="resign-type" value={f.exit_reason}
                    onChange={(e) => set('exit_reason', e.target.value)}
                    className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full">
              <option value="Resigned">Resigned</option>
              <option value="Left">Left</option>
            </select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-[#5A6377] mb-1 block">
              Reason of Leaving <span className="text-[#E11D2A]">*</span>
            </Label>
            <Input data-testid="resign-reason" value={f.exit_remarks} maxLength={200}
                   onChange={(e) => set('exit_remarks', e.target.value)}
                   placeholder="e.g. Better opportunity, End of contract, Health reasons"
                   className={(touched && reasonMissing) ? 'border-[#E11D2A]' : ''} />
            {touched && reasonMissing ? (
              <p data-testid="resign-reason-error" className="text-[11px] text-[#E11D2A] mt-1">
                Reason of Leaving is required — the monthly exit report is built from it.
              </p>
            ) : (
              <p className="text-[11px] text-[#98A1B2] mt-1">
                Mandatory. Appears on the profile and in the monthly exit report.
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold text-[#5A6377] mb-1 block">
              Note <span className="font-normal text-[#98A1B2]">(optional)</span>
            </Label>
            <Input data-testid="resign-note" value={f.note} maxLength={300}
                   onChange={(e) => set('note', e.target.value)}
                   placeholder="Handover, settlement, anything worth keeping on the record" />
          </div>

          {preview && (
            <div data-testid="resign-preview"
                 className="text-[12px] text-[#15307F] bg-[#F1F5FD] border border-[#DBE6FB] rounded-lg p-3">
              {preview}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button data-testid="resign-submit" onClick={submit}
                    disabled={saving || reasonMissing || noLwd || datesWrong}
                    className="bg-[#E11D2A] hover:bg-[#C2151F]">
              {saving ? 'Recording…' : 'Record resignation'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
